import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Copy,
  Database,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  apiClient,
  type DbPoolEntryDto,
  type DbPoolStatsDto,
  type DbProcessEntryDto,
  type DbServerStatsDto,
} from '@/api/client';
import { useDmAvailable, useTenants } from '@/features/platform/api';
import { ResourceUsageBar } from '@/features/platform/components/ResourceUsageBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shell/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const POOL_STATS_QUERY_KEY = ['system', 'pool-stats'] as const;
const STALE_TIME_MS = 5_000;
const LIVE_POLL_MS = 5_000;
const SATURATION_WARN_RATIO = 0.9;
const LIVE_TOGGLE_STORAGE_KEY = 'ptv2:admin:database:live';

function readLivePref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LIVE_TOGGLE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLivePref(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LIVE_TOGGLE_STORAGE_KEY, next ? '1' : '0');
  } catch {
    // private mode / quota — accept the loss
  }
}

function formatRelative(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const deltaSec = Math.floor((Date.now() - ms) / 1000);
  if (deltaSec < 60) return `${String(deltaSec)}s ago`;
  if (deltaSec < 3600) return `${String(Math.floor(deltaSec / 60))}m ago`;
  if (deltaSec < 86_400) return `${String(Math.floor(deltaSec / 3600))}h ago`;
  return `${String(Math.floor(deltaSec / 86_400))}d ago`;
}

function formatMs(ms: number): string {
  if (ms === 0) return 'off';
  if (ms < 1000) return `${String(ms)}ms`;
  return `${String(Math.round(ms / 1000))}s`;
}

function ratioToTone(ratio: number): 'green' | 'amber' | 'red' {
  if (ratio >= SATURATION_WARN_RATIO) return 'red';
  if (ratio >= 0.6) return 'amber';
  return 'green';
}

const TONE_BAR: Record<'green' | 'amber' | 'red', string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

export function AdminDatabasePage(): ReactElement {
  const { t } = useTranslation();
  const { available: dmAvailable, health } = useDmAvailable();
  const [live, setLive] = useState<boolean>(readLivePref);

  useEffect(() => {
    writeLivePref(live);
  }, [live]);

  const query = useQuery({
    queryKey: POOL_STATS_QUERY_KEY,
    queryFn: ({ signal }) => apiClient.getDbPoolStats({ signal }),
    staleTime: STALE_TIME_MS,
    refetchInterval: live ? LIVE_POLL_MS : false,
  });

  const data = query.data;

  const handleRefresh = useCallback(() => {
    query.refetch().catch(() => undefined);
  }, [query]);

  const capturedAgo = data !== undefined ? formatRelative(data.captured_at) : null;

  return (
    <>
      <PageHeader
        icon={<Database className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.database.statsHeading')}
        subtitle={
          capturedAgo !== null
            ? t('routes.admin.database.capturedAt', { ago: capturedAgo })
            : t('routes.admin.database.subheading')
        }
        actions={
          <>
            <Button
              size="sm"
              variant={live ? 'default' : 'outline'}
              onClick={() => {
                setLive((prev) => !prev);
              }}
              aria-pressed={live}
            >
              <span
                className={cn(
                  'mr-1.5 inline-block h-2 w-2 rounded-full',
                  live ? 'bg-emerald-300 animate-pulse' : 'bg-muted-foreground/40',
                )}
                aria-hidden="true"
              />
              {live
                ? t('routes.admin.database.live')
                : t('routes.admin.database.liveOff')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleRefresh} disabled={query.isFetching}>
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
              {t('routes.admin.database.refresh')}
            </Button>
          </>
        }
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        {/* PTV2-1383: override RAM (deployment-manager) — moved here from the tenants header. */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('routes.admin.database.overrideRamHeading')}
          </h2>
          {dmAvailable && health.resourceUsage !== null ? (
            <Card>
              <CardContent className="pt-6">
                <ResourceUsageBar
                  usedBytes={health.resourceUsage.overrideMemoryBytes}
                  ceilingBytes={health.resourceUsage.overrideMemoryCeilingBytes}
                />
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('routes.admin.database.overrideRamUnavailable')}
            </p>
          )}
        </section>

        <section className="space-y-5">
          <h2 className="text-sm font-semibold text-foreground">
            {t('routes.admin.database.databaseHeading')}
          </h2>
          {query.isPending && <PageSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.admin.database.error.heading')}
              message={t('routes.admin.database.error.message')}
              codeLabel={t('routes.admin.users.error.codeLabel')}
              onRetry={handleRefresh}
              retryLabel={t('routes.admin.database.error.retry')}
            />
          </div>
        )}

        {query.isSuccess && data !== undefined && (
          <SuccessView
            data={data}
            onCopy={() => {
              copyJson(data);
            }}
          />
        )}
        </section>
      </div>
    </>
  );
}

interface SuccessViewProps {
  readonly data: DbPoolStatsDto;
  readonly onCopy: () => void;
}

function SuccessView({ data, onCopy }: SuccessViewProps): ReactElement {
  const { t } = useTranslation();

  // Drift-style detector: only show the starvation banner when pending has been non-zero for
  // *two consecutive polls*. Single transient blips don't deserve a banner; sustained pending
  // is a real problem.
  const hasPendingNow = data.pools.some((p) => p.threads_awaiting > 0);
  const previousPendingRef = useRef<boolean>(false);
  const showStarvedBanner = hasPendingNow && previousPendingRef.current;
  useEffect(() => {
    previousPendingRef.current = hasPendingNow;
  }, [hasPendingNow]);

  const starvedPoolNames = data.pools
    .filter((p) => p.threads_awaiting > 0)
    .map((p) => p.pool_name ?? p.tenant_key ?? '?')
    .join(', ');

  const totalActive = data.pools.reduce((sum, p) => sum + p.active_connections, 0);
  const totalMax = data.pools.reduce((sum, p) => sum + p.maximum_pool_size, 0);
  const summaryRatio = totalMax > 0 ? totalActive / totalMax : 0;

  const serverConnected = data.server.threads_connected ?? 0;
  const serverMax = data.server.max_connections ?? 0;
  const serverRatio = serverMax > 0 ? serverConnected / serverMax : 0;
  const showSaturationBanner = serverRatio >= SATURATION_WARN_RATIO;

  // PTV2-1433: label each pool by the tenant it serves. Pools are keyed by tenant UUID (or the
  // sentinel "_default" for the global/cluster pool); resolve the UUID to the human tenant name via
  // the platform tenants list so the card shows "Tenant: tenant1" / "Global / Cluster" instead of a
  // bare pool name.
  const { data: tenantList } = useTenants(0, 200);
  const tenantNameByUuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const tnt of tenantList?.tenants ?? []) {
      m.set(tnt.uuid, tnt.name);
    }
    return m;
  }, [tenantList]);
  const tenantLabelFor = useCallback(
    (key: string | null): string => {
      if (key === '_default') return t('routes.admin.database.pool.globalCluster');
      if (key === null || key === '') return t('routes.admin.database.pool.unknownTenant', { id: '—' });
      const name = tenantNameByUuid.get(key);
      return name !== undefined
        ? t('routes.admin.database.pool.tenantLabel', { name })
        : t('routes.admin.database.pool.unknownTenant', { id: key.slice(0, 8) });
    },
    [t, tenantNameByUuid],
  );

  return (
    <>
      {showSaturationBanner && (
        <Banner tone="red">
          {t('routes.admin.database.saturationBanner')}
        </Banner>
      )}
      {showStarvedBanner && (
        <Banner tone="amber">
          {t('routes.admin.database.starvedBanner', { pools: starvedPoolNames })}
        </Banner>
      )}

      <SummaryCard
        active={totalActive}
        max={totalMax}
        ratio={summaryRatio}
        poolCount={data.pools.length}
      />

      <ServerCard server={data.server} />

      <PoolsSection pools={data.pools} tenantLabelFor={tenantLabelFor} />

      <ProcessListSection processList={data.process_list} />

      <div className="flex justify-end">
        <CopyJsonButton onClick={onCopy} />
      </div>
    </>
  );
}

interface BannerProps {
  readonly tone: 'red' | 'amber';
  readonly children: React.ReactNode;
}

function Banner({ tone, children }: BannerProps): ReactElement {
  const classes =
    tone === 'red'
      ? 'border-red-300 bg-red-50 text-red-900'
      : 'border-amber-300 bg-amber-50 text-amber-900';
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3 text-sm',
        classes,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

interface SummaryCardProps {
  readonly active: number;
  readonly max: number;
  readonly ratio: number;
  readonly poolCount: number;
}

function SummaryCard({ active, max, ratio, poolCount }: SummaryCardProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle className="text-base">
            {t('routes.admin.database.summary.title')}
          </CardTitle>
          <div className="text-sm text-muted-foreground">
            {t('routes.admin.database.summary.poolCount', { count: poolCount })}
          </div>
        </div>
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-2xl font-semibold tabular-nums">
            {t('routes.admin.database.summary.ratio', { active, max })}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <UsageBar ratio={ratio} />
      </CardContent>
    </Card>
  );
}

interface UsageBarProps {
  readonly ratio: number;
  /** Rendered height — the small variant fits in a process-list cell. */
  readonly size?: 'lg' | 'sm';
}

function UsageBar({ ratio, size = 'lg' }: UsageBarProps): ReactElement {
  const clamped = Math.max(0, Math.min(1, ratio));
  const tone = ratioToTone(clamped);
  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-full bg-muted',
        size === 'lg' ? 'h-2.5' : 'h-1.5',
      )}
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn('h-full rounded-full transition-[width]', TONE_BAR[tone])}
        style={{ width: `${String(clamped * 100)}%` }}
      />
    </div>
  );
}

function ServerCard({ server }: { readonly server: DbServerStatsDto }): ReactElement {
  const { t } = useTranslation();
  const connected = server.threads_connected ?? 0;
  const max = server.max_connections ?? 0;
  const peak = server.max_used_connections ?? 0;
  const ratio = max > 0 ? connected / max : 0;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t('routes.admin.database.serverHeading')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <UsageBar ratio={ratio} size="sm" />
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
          <Stat label={t('routes.admin.database.server.threadsConnected')} value={connected} />
          <Stat label={t('routes.admin.database.server.threadsRunning')} value={server.threads_running ?? '—'} />
          <Stat label={t('routes.admin.database.server.maxUsedConnections')} value={peak} />
          <Stat label={t('routes.admin.database.server.maxConnections')} value={max} />
          <Stat
            label={t('routes.admin.database.server.abortedConnects')}
            value={server.aborted_connects ?? '—'}
          />
        </dl>
      </CardContent>
    </Card>
  );
}

function PoolsSection({
  pools,
  tenantLabelFor,
}: {
  readonly pools: readonly DbPoolEntryDto[];
  readonly tenantLabelFor: (key: string | null) => string;
}): ReactElement {
  const { t } = useTranslation();
  if (pools.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t('routes.admin.database.empty')}
      </p>
    );
  }
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t('routes.admin.database.poolsHeading')}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {pools.map((pool) => (
          <PoolCard
            key={pool.pool_name ?? pool.tenant_key ?? Math.random().toString()}
            pool={pool}
            tenantLabel={tenantLabelFor(pool.tenant_key)}
          />
        ))}
      </div>
    </section>
  );
}

function PoolCard({
  pool,
  tenantLabel,
}: {
  readonly pool: DbPoolEntryDto;
  readonly tenantLabel: string;
}): ReactElement {
  const { t } = useTranslation();
  const ratio =
    pool.maximum_pool_size > 0 ? pool.active_connections / pool.maximum_pool_size : 0;
  const starved = pool.threads_awaiting > 0;
  return (
    <Card className={cn(starved && 'border-amber-300')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground/80">{tenantLabel}</p>
            <CardTitle className="font-mono text-sm font-semibold">
              {pool.pool_name ?? pool.tenant_key ?? '—'}
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">
              {pool.leak_detection_threshold_ms === 0
                ? t('routes.admin.database.leakDetectorOff')
                : t('routes.admin.database.leakDetectorOnSeconds', {
                    seconds: Math.round(pool.leak_detection_threshold_ms / 1000),
                  })}
            </p>
          </div>
          {starved && (
            <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">
              {t('routes.admin.database.starvedBadge')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <UsageBar ratio={ratio} />
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
          <Stat label={t('routes.admin.database.pool.active')} value={pool.active_connections} />
          <Stat label={t('routes.admin.database.pool.idle')} value={pool.idle_connections} />
          <Stat label={t('routes.admin.database.pool.total')} value={pool.total_connections} />
          <Stat
            label={t('routes.admin.database.pool.pending')}
            value={pool.threads_awaiting}
            tone={starved ? 'amber' : 'default'}
          />
          <Stat label={t('routes.admin.database.pool.max')} value={pool.maximum_pool_size} />
          <Stat label={t('routes.admin.database.pool.min')} value={pool.minimum_idle} />
          <Stat
            label={t('routes.admin.database.pool.connectionTimeout')}
            value={formatMs(pool.connection_timeout_ms)}
          />
          <Stat label={t('routes.admin.database.pool.maxLifetime')} value={formatMs(pool.max_lifetime_ms)} />
        </dl>
      </CardContent>
    </Card>
  );
}

interface StatProps {
  readonly label: string;
  readonly value: string | number;
  readonly tone?: 'default' | 'amber';
}

function Stat({ label, value, tone = 'default' }: StatProps): ReactElement {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'font-mono tabular-nums',
          tone === 'amber' ? 'text-amber-700 font-semibold' : '',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function ProcessListSection({
  processList,
}: {
  readonly processList: readonly DbProcessEntryDto[];
}): ReactElement {
  const { t } = useTranslation();
  const [hostFilter, setHostFilter] = useState<string>('');

  const filtered = useMemo(() => {
    const f = hostFilter.trim().toLowerCase();
    if (f === '') return processList;
    return processList.filter((row) => (row.host ?? '').toLowerCase().includes(f));
  }, [hostFilter, processList]);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('routes.admin.database.processHeading')}
        </h2>
        <div className="flex items-center gap-2">
          <Input
            type="search"
            placeholder={t('routes.admin.database.processFilter')}
            value={hostFilter}
            onChange={(e) => {
              setHostFilter(e.target.value);
            }}
            className="h-8 w-56 text-xs"
          />
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground" role="status">
              {t('routes.admin.database.process.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.id')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.user')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.host')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.db')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.command')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('routes.admin.database.process.time')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.state')}</th>
                    <th className="px-3 py-2 font-medium">{t('routes.admin.database.process.info')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.id ?? `${row.host ?? '?'}:${row.user ?? '?'}:${String(row.time_seconds ?? 0)}`}
                      className="border-b border-border last:border-0"
                    >
                      <td className="px-3 py-1.5 font-mono text-[11px]">{row.id ?? '—'}</td>
                      <td className="px-3 py-1.5">{row.user ?? '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{row.host ?? '—'}</td>
                      <td className="px-3 py-1.5">{row.db ?? '—'}</td>
                      <td className="px-3 py-1.5">{row.command ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {row.time_seconds ?? '—'}
                      </td>
                      <td className="px-3 py-1.5">{row.state ?? '—'}</td>
                      <td className="px-3 py-1.5 max-w-[24rem] truncate" title={row.info ?? undefined}>
                        {row.info ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function CopyJsonButton({ onClick }: { readonly onClick: () => void }): ReactElement {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<boolean>(false);
  const handle = useCallback(() => {
    onClick();
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  }, [onClick]);
  return (
    <Button size="sm" variant="outline" onClick={handle}>
      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      {copied ? t('routes.admin.database.copied') : t('routes.admin.database.copyJson')}
    </Button>
  );
}

function copyJson(data: DbPoolStatsDto): void {
  void navigator.clipboard.writeText(JSON.stringify(data, null, 2)).catch(() => undefined);
}

function PageSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}
